import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { DataHelpers } from '../../DataHelpers';
import { TokenHelper } from '../../TokenHelper';
import { IsNeomyticConsultant, PeppolInvoiceResponse } from '../../interfaces/Consultant';
import { dataActionCreators, Invoice, Sale, TimeSheetEntry } from '../../store/Data';
import { TeamsState } from '../../store/TeamsState';
import { Button, makeStyles, MessageBar, MessageBarBody, Text, TableColumnDefinition, createTableColumn, DataGrid, DataGridBody, DataGridRow, DataGridCell, TableColumnSizingOptions } from '@fluentui/react-components';
import { Dismiss24Filled, Edit24Filled, ArrowUpload24Filled } from "@fluentui/react-icons";
import { useQuickViewStyles } from '../Styles/QuickView';
import { DocumentOnePageAddIcon, useTabStyles } from '../Styles/Editor';
import { SharePointSVG } from '../Resources/SharePointSVG';
import { DownloadSPOLinkAndNav } from '../Tools/FileDownloadHelper';
import { useArticles, useConsultants, useCustomers, useProducts, useProjects, useProjectsAssigments, useSales, useTimeSheetEntries } from '../Tools/ObjectLoader';

const useStyles = makeStyles({
    gridCellDate: {
    },
    gridCellDesc: {
        flexGrow: '3'
    },
    gridCellPrice: {
    }
});

function sortByDate(a: TimeSheetEntry, b: TimeSheetEntry) {
    const sl1: number = a.date ? (new Date(a.date)).getTime() : Date.now();
    const sl2: number = b.date ? (new Date(b.date)).getTime() : Date.now();
    if (sl1 < sl2) {
        return 1;
    }
    if (sl1 > sl2) {
        return -1;
    }

    // names must be equal
    return 0;
}
function sortByDate2(a: Sale, b: Sale) {
    const sl1: number = a.date ? (new Date(a.date)).getTime() : Date.now();
    const sl2: number = b.date ? (new Date(b.date)).getTime() : Date.now();
    if (sl1 < sl2) {
        return 1;
    }
    if (sl1 > sl2) {
        return -1;
    }

    // names must be equal
    return 0;
}

interface Elem {
    totalPrice: number,
    items: TableElem[],
}

interface TableElem {
    id: string,
    date: string,
    desc: string[],
    price: string,
}

const columnSizingOptions: TableColumnSizingOptions = {
    Date: {
        minWidth: 90,
        defaultWidth: 90,
        idealWidth: 90,
    },
    Desc: {
        idealWidth: 400,
    },
    Price: {
        defaultWidth: 100,
        minWidth: 100,
        idealWidth: 100,
    },
};

interface PeppolInvoiceHistory extends PeppolInvoiceResponse {
    decodedTimeStamp: Date,
}
const PeppolInvoiceHistoryByDate = (a: PeppolInvoiceHistory, b: PeppolInvoiceHistory): number => {
    const sl1: number = a.decodedTimeStamp.getTime();
    const sl2: number = b.decodedTimeStamp.getTime();

    if (sl1 > sl2) {
        return 1;
    }
    if (sl1 < sl2) {
        return -1;
    }
    return 0;
};
function hasMilliseconds(input: string): boolean { return /\.\d{1,3}(?:Z|[+-]\d{2}:?\d{2})?$/.test(input); }
function GetPeppolInvoiceHistory(invoice: Invoice): PeppolInvoiceHistory[] {
    const h: PeppolInvoiceHistory[] = [];
    if (invoice.peppolSendedDocs) {
        invoice.peppolSendedDocs.forEach(d => {
            const timeStamp = new Date(d.peppoDocSendDate + "Z");
            if (!hasMilliseconds(d.peppoDocSendDate)) {
                timeStamp.setMilliseconds(999);
            }
            h.push({
                timeStamp: d.peppoDocSendDate,
                decodedTimeStamp: timeStamp,
                code: "SEND",
                peppolReceivedDocsId: d.peppolSendedDocsId
            });
            d.peppolSendedDocReplies?.forEach(dd => {
                const replyTimeStamp = new Date(dd.timeStamp + "Z");
                if (!hasMilliseconds(dd.timeStamp)) {
                    replyTimeStamp.setMilliseconds(999);
                }
                h.push({
                    timeStamp: dd.timeStamp,
                    decodedTimeStamp: replyTimeStamp,
                    code: dd.code,
                    peppolReceivedDocsId: dd.peppolReceivedDocsId
                });
            });
        });
    }
    if (invoice.peppolInvoiceResponse) {
        invoice.peppolInvoiceResponse.forEach(d => {
            const responseTimeStamp = new Date(d.timeStamp + "Z");
            if (!hasMilliseconds(d.timeStamp)) {
                responseTimeStamp.setMilliseconds(999);
            }
            h.push({
                timeStamp: d.timeStamp,
                decodedTimeStamp: responseTimeStamp,
                code: d.code,
                peppolReceivedDocsId: d.peppolReceivedDocsId
            });
        });
    }

    return h.sort(PeppolInvoiceHistoryByDate);
}

export const InvoicesQuickView = () => {
    const { customers } = useCustomers();
    const invoiceQuickView = useSelector((state: ApplicationState) => state.data.invoiceQuickView);
    const { timeSheetEntries, timeSheetEntriesLoading } = useTimeSheetEntries();
    const { projectsAssigments, projectsAssigmentsLoading } = useProjectsAssigments();
    const { projects, projectsLoading } = useProjects();
    const { consultants, consultantsLoading } = useConsultants();
    const { products, productsLoading } = useProducts();
    const { articles, articlesLoading } = useArticles();
    const { sales, salesLoading } = useSales();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { instance } = useMsal();
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const viewDebugInfo = useSelector((state: ApplicationState) => state.userNeomyticLogin.viewDebugInfo);
    const styles = useStyles();
    const stylesQV = useQuickViewStyles();
    const stylesTab = useTabStyles();

    const c = React.useMemo(() =>
        invoiceQuickView ? DataHelpers.GetCustomerFromId(customers, invoiceQuickView.customerid) : null
        , [invoiceQuickView, customers]);
    const peppolInvoiceHistory = React.useMemo(() =>
        invoiceQuickView ? GetPeppolInvoiceHistory(invoiceQuickView) : []
        , [invoiceQuickView]);

    const IsNeomyticInvoice = React.useMemo(() => {
        if (invoiceQuickView) return IsNeomyticConsultant(invoiceQuickView.consultantid);
        return false;
    }, [invoiceQuickView]);

    const columns: TableColumnDefinition<TableElem>[] = [
        createTableColumn<TableElem>({
            columnId: "Date",
            renderHeaderCell: () => {
                return "Date";
            },
            renderCell: (item) => {
                return item.date;
            },
        }),
        createTableColumn<TableElem>({
            columnId: "Desc",
            renderHeaderCell: () => {
                return "Desc";
            },

            renderCell: (item) => {
                return <div className={stylesQV.Column}>
                    {item.desc.map(s =>
                        <Text truncate>{s}</Text>
                    )}
                </div>;
            },
        }),
        createTableColumn<TableElem>({
            columnId: "Price",
            renderHeaderCell: () => {
                return "Price";
            },
            renderCell: (item) => {
                return item.price;
            },
        }),
    ];

    const [invoiceItemsMemo, setInvoiceItemsMemo] = React.useState<Elem>({ items: [], totalPrice: 0 });
    React.useEffect(() => {
        let TotalPrice = 0;
        const items: TableElem[] = [];
        if (invoiceQuickView && timeSheetEntries && projectsAssigments && projects && consultants) {
            timeSheetEntries
                .filter(ts => (IsNeomyticInvoice && ts.invoiceid === invoiceQuickView.invoiceid) || (!IsNeomyticInvoice && ts.consultantInvoiceid === invoiceQuickView.invoiceid))
                .sort(sortByDate)
                .forEach((t) => {
                    const pa = projectsAssigments ? projectsAssigments.find(e => e.projectAssignmentid === t.projectAssignmentid) : undefined;
                    const p = pa && projects ? projects.find(e => e.projectid === pa.projectid) : undefined;
                    const co = pa && consultants ? consultants.find(e => e.consultantid === pa.consultantid) : undefined;
                    const price: number = pa ? Math.round((IsNeomyticInvoice ? pa.customerPrice : pa.consultantPrice) * t.count * 100) / 100 : 0;
                    TotalPrice = TotalPrice + price;
                    items.push({
                        id: t.timesheetEntryid ? t.timesheetEntryid : "-",
                        date: t.date ? new Date(t.date).toLocaleDateString() : "-",
                        desc: [p && p.displayName ? p.displayName : "",
                        pa && pa.displayName ? pa.displayName : "",
                        co && co.displayName ? co.displayName : "",
                        t.description ? t.description : "",
                        t.count + "/" + (pa ? pa.unitOfTime : "?")
                        ],
                        price: price.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })
                    });
                });
            setInvoiceItemsMemo({ items: items, totalPrice: TotalPrice });
        }
    }, [invoiceQuickView, timeSheetEntries, projectsAssigments, projects, consultants]);

    const [itemsArticlesMemo, setItemsArticlesMemo] = React.useState<Elem>({ items: [], totalPrice: 0 });
    React.useEffect(() => {
        let TotalPrice = 0;
        const itemsArticles: TableElem[] = [];
        if (invoiceQuickView && sales && articles) {
            sales.filter(ts => ts.invoiceid === invoiceQuickView.invoiceid)
                .sort(sortByDate2)
                .forEach((t) => {
                    const a = articles ? articles.find(e => e.articleid === t.articleid) : undefined;
                    const price: number = Math.round(t.unitPrice * t.count * 100) / 100;
                    TotalPrice = TotalPrice + price;
                    itemsArticles.push({
                        id: t.sellid ? t.sellid : "-",
                        date: t.date ? new Date(t.date).toLocaleDateString() : "-",
                        desc: [a && a.displayName ? a.displayName : "",
                        t && t.description ? t.description : "",
                        t.count.toString()],
                        price: price.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })
                    });
                });
            setItemsArticlesMemo({ items: itemsArticles, totalPrice: TotalPrice });
        }
    }, [invoiceQuickView, sales, articles]);

    const [invoiceEntriesMemo, setInvoiceEntriesMemo] = React.useState<Elem>({ items: [], totalPrice: 0 });
    React.useEffect(() => {
        let TotalPrice = 0;
        const itemsArticles: TableElem[] = [];
        if (invoiceQuickView && invoiceQuickView.invoiceEntries) {
            invoiceQuickView.invoiceEntries.filter(ts => ts.invoiceid === invoiceQuickView.invoiceid)
                .forEach((t) => {
                    const price: number = Math.round(t.sellPrice * t.count * 100) / 100;
                    TotalPrice = TotalPrice + price;
                    itemsArticles.push({
                        id: t.invoiceEntryid ? t.invoiceEntryid : "-",
                        date: "-",
                        desc: [t.description ? t.description : "-"],
                        price: price.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })
                    });
                });
            setInvoiceEntriesMemo({ items: itemsArticles, totalPrice: TotalPrice });
        }
    }, [invoiceQuickView]);

    if (invoiceQuickView === null) {
        return null;
    }

    const tva = (invoiceQuickView.vatcode / 100);
    const TotalPrice = invoiceItemsMemo.totalPrice + itemsArticlesMemo.totalPrice + invoiceEntriesMemo.totalPrice;

    console.log(peppolInvoiceHistory);
    return (
        <div className={stylesQV.root} >
            <div className={stylesQV.titleAndOps}>
                <h2>{t('Invoice') + " : " + invoiceQuickView.invoiceRef}</h2>
                <div className={stylesQV.titleAndOpsButtons}>
                    <Button icon={<Edit24Filled />} shape="circular" title={t('Edit')} onClick={() => dispatch(dataActionCreators.requestInvoiceEdition(invoiceQuickView))} />
                    <Button icon={<Dismiss24Filled />} shape="circular" title="Close" onClick={() => dispatch(dataActionCreators.requestClearQuickView())} />
                </div>
            </div>
            {viewDebugInfo &&
                <>
                    <h2>Id</h2>
                    <Text truncate>{invoiceQuickView.invoiceid}</Text>
                </>
            }
            {IsNeomyticInvoice &&
                <>
                    <h2>{t('Invoice')} Doc</h2>
                    <div className={stylesQV.InlineRow}>
                        {invoiceQuickView.invoiceSharePointFileUniqueId
                            ? <Button shape="circular" title={t('ViewInSharePoint')} icon={<SharePointSVG />}
                                onClick={() => {
                                    TokenHelper.GetToken(instance, teamsState)
                                        .then(token => {
                                            if (token !== null && invoiceQuickView.invoiceid) {
                                                DownloadSPOLinkAndNav('api/Invoices/' + invoiceQuickView.invoiceid + "/DownloadSPOLink", token);
                                            }
                                        });
                                }
                                } />
                            : <MessageBar intent="warning">
                                <MessageBarBody>
                                    {t('Missing')}
                                </MessageBarBody>
                            </MessageBar>
                        }
                        <Button icon={<ArrowUpload24Filled />} shape="circular" title="Upload" onClick={() => dispatch(dataActionCreators.requestFileUploadInvoice(invoiceQuickView))} />
                        <Button icon={<DocumentOnePageAddIcon />} shape="circular" title={t('GenInvoice')} onClick={() => dispatch(dataActionCreators.requestInvoiceGeneration(invoiceQuickView))} />
                    </div>
                </>
            }
            <h2>{t('Customers')}</h2>
            <Text truncate>{c ? c.displayName : ""}</Text>
            {invoiceItemsMemo.items.length > 0 && (<>
                <h2>Services</h2>
                <DataGrid
                    items={invoiceItemsMemo.items}
                    columns={columns}
                >
                    <DataGridBody<TableElem>>
                        {({ item }) => (
                            <DataGridRow<TableElem>
                                key={item.id}
                            >
                                {({ renderCell, columnId }) => (
                                    <DataGridCell
                                        className={columnId === 'Date' ? styles.gridCellDate : columnId === 'Desc' ? styles.gridCellDesc : styles.gridCellPrice}
                                    >{renderCell(item)}</DataGridCell>
                                )}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
            </>
            )}
            {itemsArticlesMemo.items.length > 0 && (<>
                <h2>Articles</h2>
                <DataGrid
                    items={itemsArticlesMemo.items}
                    columns={columns}
                >
                    <DataGridBody<TableElem>>
                        {({ item }) => (
                            <DataGridRow<TableElem>
                                key={item.id}
                            >
                                {({ renderCell, columnId }) => (
                                    <DataGridCell
                                        className={columnId === 'Date' ? styles.gridCellDate : columnId === 'Desc' ? styles.gridCellDesc : styles.gridCellPrice}
                                    >{renderCell(item)}</DataGridCell>
                                )}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
            </>
            )}
            {invoiceEntriesMemo.items.length > 0 && (<>
                <h2>Free entries</h2>
                <DataGrid
                    resizableColumns
                    items={invoiceEntriesMemo.items}
                    columns={columns}
                    columnSizingOptions={columnSizingOptions}
                >
                    <DataGridBody<TableElem>>
                        {({ item }) => (
                            <DataGridRow<TableElem>
                                key={item.id}
                            >
                                {({ renderCell, columnId }) => (
                                    <DataGridCell
                                        className={columnId === 'Date' ? styles.gridCellDate : columnId === 'Desc' ? styles.gridCellDesc : styles.gridCellPrice}
                                    >{renderCell(item)}</DataGridCell>
                                )}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
            </>
            )}
            <h2>{t('Billed')}</h2>
            <Text>{"Sub Total = " + TotalPrice.toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            <Text>{"VAT = " + (TotalPrice * tva).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            <Text>{"Total = " + (TotalPrice * (1 + tva)).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
            <h2>{t('Paid')}</h2>
            {invoiceQuickView.payment > 0
                ? <div className={stylesQV.InlineRow}>
                    {
                        invoiceQuickView.payment.toFixed(2) == (TotalPrice * (1 + tva)).toFixed(2)
                            ? <Text className={stylesTab.SuccessText}>{t('Paid')}</Text>
                            : <Text className={stylesTab.WarningText}>{t('Balance')}</Text>
                    }
                    <div className={stylesQV.Column}>
                        {invoiceQuickView.paymentDate && <Text>{new Date(invoiceQuickView.paymentDate).toLocaleDateString()}</Text>}
                        <Text>{"Total = " + (invoiceQuickView.payment).toLocaleString(undefined, { style: 'currency', currency: 'EUR' })}</Text>
                    </div>
                </div>
                :
                <Text className={stylesTab.DangerText}>{t('NotPaid')}</Text>
            }
            {invoiceQuickView.description && <>
                <h2>Description</h2>
                <pre style={{ whiteSpace: "pre-wrap" }}>{invoiceQuickView.description}</pre>
            </>}
            {invoiceQuickView.invoiceDescription && <>
                <h2>{t('InvoiceDescription')}</h2>
                <pre style={{ whiteSpace: "pre-wrap" }}>{invoiceQuickView.invoiceDescription}</pre>
            </>}
            {peppolInvoiceHistory.length > 0 && <>
                <h2>Peppol history</h2>
                {peppolInvoiceHistory.map((h) => {
                    return <div key={"peppolInvoiceHistory" + h.timeStamp} className={stylesQV.InlineRow}>
                        <Text>{h.decodedTimeStamp.toLocaleDateString() + " " + h.decodedTimeStamp.toLocaleTimeString()}</Text>
                        <Text>{t(h.code, { ns: 'PeppolStatusCode' })}</Text>
                    </div>;
                })}
            </>
            }
        </div>
    );
};